import { useState } from "react"
import {Calculator as CalcIcon, Copy, Check, FlaskConical} from 'lucide-react'
import {
  getFullCalculation,
  gemstoneData,
  qualityGrades,
  shapeMeta,
  type GemstoneType,
  type ShapeType,
  type QualityGrade,
} from "@/utils/gemstone"
import toast from "react-hot-toast"

interface CalculatorProps {
  defaultGemstone?: GemstoneType
  title?: string
  description?: string
}

function Calculator({ defaultGemstone, title, description }: CalculatorProps) {
  const [shape, setShape] = useState<ShapeType>("round")
  const [length, setLength] = useState("")
  const [width, setWidth] = useState("")
  const [depth, setDepth] = useState("")
  const [gemstoneType, setGemstoneType] = useState<GemstoneType>(defaultGemstone || "sapphire")
  const [quality, setQuality] = useState<QualityGrade>("mid")
  const [result, setResult] = useState<ReturnType<typeof getFullCalculation> | null>(null)
  const [copied, setCopied] = useState(false)

  const parseDimensions = () => {
    const l = parseFloat(length)
    const w = parseFloat(width)
    const d = parseFloat(depth)
    if (!l || !w || !d || l <= 0 || w <= 0 || d <= 0) return null
    return { l, w, d }
  }

  const handleCalculate = () => {
    const dims = parseDimensions()
    if (!dims) {
      toast.error("Please enter valid dimensions")
      return
    }
    setResult(getFullCalculation(shape, dims.l, dims.w, dims.d, gemstoneType, quality))
  }

  const handleQualityChange = (grade: QualityGrade) => {
    setQuality(grade)
    const dims = parseDimensions()
    if (!dims || !result) return
    setResult(getFullCalculation(shape, dims.l, dims.w, dims.d, gemstoneType, grade))
  }

  const copyResults = () => {
    if (!result) return

    const f = result.formula
    const text = `Gemstone Calculator estimate
Gemstone: ${f.gemName}
Shape: ${f.shapeName}
Dimensions: ${f.length}mm × ${f.width}mm × ${f.depth}mm
Quality grade: ${f.qualityLabel}
Formula: ${f.length} × ${f.width} × ${f.depth} × ${f.shapeFactor} × SG ${f.specificGravity}
Estimated weight: ${result.caratWeight} ct
Price per carat (${f.qualityLabel}): $${f.pricePerCarat.toLocaleString()}
Value estimate: $${result.estimatedValue.toLocaleString()}
Market range: $${result.valueRange.low.toLocaleString()} – $${result.valueRange.high.toLocaleString()}
This is an estimate, not a formal appraisal.`

    navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success("Results copied to clipboard!")
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
      <div className="bg-gradient-to-r from-emerald-600 to-blue-700 px-8 py-6">
        <div className="flex items-center gap-3">
          <CalcIcon className="w-8 h-8 text-white" />
          <div>
            <h2 className="text-2xl font-bold text-white">
              {title || "Gemstone Weight & Value Calculator"}
            </h2>
            <p className="text-emerald-50 text-sm mt-1">
              {description || "Calculate carat weight and estimated market value"}
            </p>
          </div>
        </div>
      </div>

      <div className="p-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="gemstone-type">
                Gemstone Type
              </label>
              <select
                id="gemstone-type"
                value={gemstoneType}
                onChange={(e) => setGemstoneType(e.target.value as GemstoneType)}
                disabled={!!defaultGemstone}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 transition-colors disabled:bg-gray-50"
              >
                {Object.entries(gemstoneData).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.name} (SG {value.sg})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="gem-shape">
                Shape
              </label>
              <select
                id="gem-shape"
                value={shape}
                onChange={(e) => setShape(e.target.value as ShapeType)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 transition-colors"
              >
                {(Object.keys(shapeMeta) as ShapeType[]).map((key) => (
                  <option key={key} value={key}>
                    {shapeMeta[key].name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="gem-length">
                  Length (mm)
                </label>
                <input
                  id="gem-length"
                  type="number"
                  step="0.01"
                  min="0"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="gem-width">
                  Width (mm)
                </label>
                <input
                  id="gem-width"
                  type="number"
                  step="0.01"
                  min="0"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="gem-depth">
                  Depth (mm)
                </label>
                <input
                  id="gem-depth"
                  type="number"
                  step="0.01"
                  min="0"
                  value={depth}
                  onChange={(e) => setDepth(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 transition-colors"
                />
              </div>
            </div>

            <fieldset>
              <legend className="block text-sm font-semibold text-gray-700 mb-2">
                Quality Grade
              </legend>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(qualityGrades) as QualityGrade[]).map((grade) => {
                  const meta = qualityGrades[grade]
                  const selected = quality === grade
                  return (
                    <button
                      key={grade}
                      type="button"
                      onClick={() => handleQualityChange(grade)}
                      aria-pressed={selected}
                      className={`rounded-xl border-2 px-3 py-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-emerald-200 ${
                        selected
                          ? "border-emerald-600 bg-emerald-50 shadow-sm"
                          : "border-gray-200 bg-white hover:border-emerald-300"
                      }`}
                    >
                      <span className={`block text-sm font-semibold ${selected ? "text-emerald-800" : "text-gray-800"}`}>
                        {meta.label}
                      </span>
                      <span className="mt-1 block text-[11px] leading-snug text-gray-500">
                        {meta.hint}
                      </span>
                    </button>
                  )
                })}
              </div>
            </fieldset>

            <button
              type="button"
              onClick={handleCalculate}
              className="w-full bg-gradient-to-r from-emerald-600 to-blue-700 text-white py-4 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-600"
            >
              Calculate Weight & Value
            </button>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl p-6 border-2 border-emerald-100 min-h-[420px]">
            {result ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Gem Weight Estimator</h3>
                  <button
                    type="button"
                    onClick={copyResults}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    title="Copy results"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-600" />
                    )}
                    {copied ? "Copied" : "Copy Results"}
                  </button>
                </div>

                <div className="bg-white rounded-lg p-5 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Estimated Carat Weight</p>
                  <p className="text-4xl font-bold text-gradient-emerald">
                    {result.caratWeight}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">carats</p>
                </div>

                <div className="bg-white rounded-lg p-5 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">
                    Value Estimate · {result.formula.qualityLabel}
                  </p>
                  <p className="text-3xl font-bold text-blue-800">
                    ${result.estimatedValue.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    ${result.formula.pricePerCarat.toLocaleString()} per carat × {result.caratWeight} ct
                  </p>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between text-xs text-gray-500">
                    <span>Market range</span>
                    <span>
                      ${result.valueRange.low.toLocaleString()} – ${result.valueRange.high.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <FlaskConical className="w-4 h-4 text-emerald-700" />
                    <p className="text-sm font-semibold text-gray-800">Weight formula</p>
                  </div>
                  <p className="font-mono text-xs sm:text-sm text-gray-800 bg-emerald-50/80 rounded-md px-3 py-2 leading-relaxed">
                    {result.formula.length} × {result.formula.width} × {result.formula.depth} × {result.formula.shapeFactor} × {result.formula.specificGravity}
                    <span className="text-emerald-800 font-semibold"> = {result.caratWeight} ct</span>
                  </p>
                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs text-gray-600">
                    <div>
                      <dt className="text-gray-500">Volume (L × W × D)</dt>
                      <dd className="font-medium text-gray-800">{result.formula.volume} mm³</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">{result.formula.shapeName} factor</dt>
                      <dd className="font-medium text-gray-800">{result.formula.shapeFactor}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">{result.formula.gemName} SG</dt>
                      <dd className="font-medium text-gray-800">{result.formula.specificGravity}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Quality</dt>
                      <dd className="font-medium text-gray-800">{result.formula.qualityLabel}</dd>
                    </div>
                  </dl>
                </div>

                <p className="text-xs text-gray-500 italic">
                  This is an estimate based on specific gravity and typical market averages — not a formal appraisal. Actual price, value, and worth vary with color, clarity, origin, and current demand.
                </p>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-center px-4">
                <div>
                  <CalcIcon className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                  <p className="text-gray-700 font-medium">
                    Enter dimensions and a quality grade, then calculate
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Weight uses volume × shape factor × specific gravity. Value uses your selected grade.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Calculator
